/**
 * Virtual pagination for the collection and search grids.
 *
 * Shopify paginates collection.products / search.results before the theme hides products
 * (should-show-product), so pages come out short and the counters include hidden products. This element:
 *  1. loads a visibility index of the whole (filtered/sorted) result set from the
 *     visibility-index section, 250 items per request, cached in sessionStorage,
 *  2. cuts the visible products into full pages (page 1 keeps a slot per inline promo),
 *  3. takes the cards for the current page from the server-rendered raw pages and
 *  4. rewrites the page numbers, "Show X results" and the banner product count.
 * ?page=N means the virtual page. Until it is assembled, what it would replace stays hidden (see
 * products-grid.css). If that takes longer than RENDER_TIMEOUT or anything fails, the server-rendered
 * page is shown as it is.
 *
 * Usage (inside #filter-results, after the grid <ul> and the pagination):
 *   <virtual-pagination hidden data-section-id="..." data-per-page="..." ...>
 */
(() => {
  const INDEX_WINDOW = 250;
  const MAX_INDEX_WINDOWS = 20;
  const MAX_PARALLEL_REQUESTS = 6;
  const CACHE_PREFIX = 'vp-index:';
  const CACHE_TTL = 10 * 60 * 1000;
  const RENDER_TIMEOUT = 4000;

  const VirtualPaging = {
    /**
     * Number of products on a page (page 1 gives one slot to each inline promo).
     * @param {number} page - Page number (1-based).
     * @param {number} perPage - Slots per page.
     * @param {number} promoSlots - Inline promos shown on page 1.
     * @returns {number}
     */
    capacity(page, perPage, promoSlots) {
      return page === 1 ? Math.max(1, perPage - promoSlots) : perPage;
    },

    /**
     * @param {number} total - Number of visible products.
     * @param {number} perPage - Slots per page.
     * @param {number} promoSlots - Inline promos shown on page 1.
     * @returns {number}
     */
    pageCount(total, perPage, promoSlots) {
      const first = VirtualPaging.capacity(1, perPage, promoSlots);
      if (total <= first) return 1;
      return 1 + Math.ceil((total - first) / perPage);
    },

    /**
     * Visible-product range [start, end) of a page.
     * @param {number} page - Page number (1-based).
     * @param {number} total - Number of visible products.
     * @param {number} perPage - Slots per page.
     * @param {number} promoSlots - Inline promos shown on page 1.
     * @returns {{start: number, end: number}}
     */
    range(page, total, perPage, promoSlots) {
      const first = VirtualPaging.capacity(1, perPage, promoSlots);
      const start = Math.min(total, page === 1 ? 0 : first + (page - 2) * perPage);
      const end = Math.min(total, start + VirtualPaging.capacity(page, perPage, promoSlots));
      return { start, end };
    },

    /**
     * Page links in the style of Shopify's paginate.parts: first, last and current ± 2,
     * gaps of more than one page become an ellipsis (null).
     * @param {number} current - Current page.
     * @param {number} pages - Number of pages.
     * @returns {Array<number|null>}
     */
    parts(current, pages) {
      const shown = new Set([1, pages]);
      for (let page = current - 2; page <= current + 2; page += 1) {
        if (page >= 1 && page <= pages) shown.add(page);
      }
      const sorted = [...shown].sort((a, b) => a - b);
      const parts = [];
      sorted.forEach((page, i) => {
        const prev = sorted[i - 1];
        if (prev !== undefined && page - prev === 2) parts.push(prev + 1);
        if (prev !== undefined && page - prev > 2) parts.push(null);
        parts.push(page);
      });
      return parts;
    },

    /**
     * Builds the list of visible products from the index windows.
     * @param {Array<{items: number, offset: number, ids: string[], flags: string}>} windows
     * @param {number} rawItems - Expected number of products (paginate.items).
     * @returns {Array<{raw: number, id: string}>} Visible items with their position in Shopify's list.
     */
    buildIndex(windows, rawItems) {
      const sorted = [...windows].sort((a, b) => a.offset - b.offset);
      const positions = [];
      let expectedOffset = 0;
      sorted.forEach((win) => {
        if (win.items !== rawItems || win.offset !== expectedOffset || win.ids.length !== win.flags.length) {
          throw new Error('Visibility index is inconsistent');
        }
        for (let i = 0; i < win.flags.length; i += 1) {
          if (win.flags[i] === '1') positions.push({ raw: win.offset + i, id: win.ids[i] });
        }
        expectedOffset += win.flags.length;
      });
      if (expectedOffset !== rawItems) throw new Error('Visibility index is incomplete');
      return positions;
    },

    /**
     * Raw (server) pages that contain the given products.
     * @param {Array<{raw: number}>} slice - Visible products of a page.
     * @param {number} perPage - Server page size.
     * @returns {number[]}
     */
    rawPages(slice, perPage) {
      return [...new Set(slice.map((pos) => Math.floor(pos.raw / perPage) + 1))].sort((a, b) => a - b);
    },

    /**
     * Places inline promos before the product at their position, leftovers at the end
     * (same rule as the Liquid render loop).
     * @param {Array} products - Product items.
     * @param {Array<{position: number, node: *}>} promos - Inline promos in block order.
     * @returns {Array}
     */
    interleavePromos(products, promos) {
      const items = [];
      products.forEach((product, i) => {
        promos.filter((promo) => promo.position === i + 1).forEach((promo) => items.push(promo.node));
        items.push(product);
      });
      promos.filter((promo) => promo.position > products.length).forEach((promo) => items.push(promo.node));
      return items;
    }
  };

  /**
   * Runs async tasks with limited concurrency, keeping the input order.
   * @param {Array} items - Task inputs.
   * @param {number} limit - Maximum number of tasks in flight.
   * @param {Function} task - Async function per item.
   * @returns {Promise<Array>}
   */
  async function mapLimit(items, limit, task) {
    const results = new Array(items.length);
    let next = 0;
    const worker = async () => {
      while (next < items.length) {
        const i = next;
        next += 1;
        results[i] = await task(items[i]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { VirtualPaging, mapLimit };
  }

  if (typeof window === 'undefined' || customElements.get('virtual-pagination')) return;

  class VirtualPagination extends HTMLElement {
    connectedCallback() {
      this.results = this.closest('#filter-results');
      this.abortController = new AbortController();
      this.isActive = false;
      this.isPending = true;
      this.timedOut = false;

      // Server-rendered pages come with data-vp-pending already; after a filter swap it is set here
      this.setPending(true);

      // facet-filters.js swaps #filter-results before it pushes the new URL, so read it a tick later.
      this.initTimer = setTimeout(() => this.init(), 0);
    }

    disconnectedCallback() {
      clearTimeout(this.initTimer);
      this.abortController.abort();
      this.isActive = false;
      this.isPending = false;
      this.setPending(false);
    }

    async init() {
      const { signal } = this.abortController;
      this.grid = this.results ? this.results.querySelector(':scope > ul') : null;
      if (!this.grid) {
        this.isPending = false;
        this.setPending(false);
        return;
      }

      this.perPage = Number(this.dataset.perPage);
      this.rawItems = Number(this.dataset.rawItems);
      this.rawPage = Number(this.dataset.rawPage);
      this.promoSlots = Number(this.dataset.promoSlots) || 0;

      const url = new URL(window.location.href);
      const requestedPage = Math.max(1, Number.parseInt(url.searchParams.get('page'), 10) || 1);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      this.params = url.searchParams;

      const timeout = setTimeout(() => {
        this.timedOut = true;
        console.warn('Virtual pagination timed out, showing the server page.');
        this.setPending(false);
      }, RENDER_TIMEOUT);

      try {
        this.cards = new Map();
        this.loadedRawPages = new Set([this.rawPage]);
        this.collectCards(this.grid);
        this.promos = [...this.grid.querySelectorAll(':scope > li[data-promo-position]')].map((node) => ({
          position: Number(node.dataset.promoPosition),
          node
        }));

        // After a timeout the index still finishes loading, so the next page view can use the cache
        this.positions = await this.loadIndex(signal);
        if (this.timedOut) return;
        this.total = this.positions.length;
        this.pages = VirtualPaging.pageCount(this.total, this.perPage, this.promoSlots);

        const page = Math.min(requestedPage, this.pages);
        if (page !== requestedPage) {
          window.history.replaceState({ searchParams: this.queryFor(page) }, '', this.pageUrl(page));
        }

        if (this.total === 0) {
          this.showEmpty();
        } else {
          await this.showPage(page, false, signal);
        }

        this.isActive = true;
        this.updateCounts();
        this.updatePagination();
        this.dispatchUpdated();
      } catch (error) {
        if (!signal.aborted && !this.timedOut) console.warn('Virtual pagination unavailable, keeping server pages.', error);
      } finally {
        clearTimeout(timeout);
        this.isPending = false;
        if (!signal.aborted) this.setPending(false);
      }
    }

    /**
     * Appends the next virtual page (used by custom-pagination.js for "load more" and infinite scroll).
     * @param {AbortSignal} [signal] - Abort signal of the caller.
     * @returns {Promise<boolean>} Whether there are more pages.
     */
    async appendNextPage(signal) {
      if (!this.isActive || this.lastPage >= this.pages) return false;
      await this.showPage(this.lastPage + 1, true, signal || this.abortController.signal);
      this.updatePagination();
      this.dispatchUpdated();
      return this.lastPage < this.pages;
    }

    /**
     * @param {boolean} pending - Hide products and pagination (true) or reveal them incl. the banner
     *   product count (false).
     */
    setPending(pending) {
      if (!this.results) return;
      if (pending) {
        this.results.dataset.vpPending = '';
        return;
      }
      delete this.results.dataset.vpPending;
      document.querySelectorAll('.js-collection-product-count[data-vp-pending]').forEach((el) => el.removeAttribute('data-vp-pending'));
    }

    /**
     * Remembers the product cards of a server-rendered page by their raw index.
     * @param {ParentNode} root - Grid or fetched document fragment.
     */
    collectCards(root) {
      root.querySelectorAll('li[data-raw-index]').forEach((li) => {
        this.cards.set(Number(li.dataset.rawIndex), li);
      });
    }

    /**
     * @param {number} page - Virtual page (1-based).
     * @returns {string} Query string for the page.
     */
    queryFor(page) {
      const params = new URLSearchParams(this.params);
      if (page > 1) params.set('page', page);
      return params.toString();
    }

    /**
     * @param {number} page - Virtual page (1-based).
     * @returns {string} URL of the page.
     */
    pageUrl(page) {
      const query = this.queryFor(page);
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    /**
     * @param {string} sectionId - Section to render.
     * @param {number} page - Server page.
     * @param {AbortSignal} signal - Abort signal.
     * @returns {Promise<DocumentFragment>}
     */
    async fetchSection(sectionId, page, signal) {
      const params = new URLSearchParams(this.params);
      params.set('page', page);
      params.set('section_id', sectionId);
      const response = await fetch(`${window.location.pathname}?${params}`, { signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      return tmpl.content;
    }

    /**
     * Loads the visibility index for the current filters and sort order.
     * @param {AbortSignal} signal - Abort signal.
     * @returns {Promise<Array<{raw: number, id: string}>>}
     */
    async loadIndex(signal) {
      const sortedParams = new URLSearchParams([...this.params].sort(([a], [b]) => a.localeCompare(b)));
      const cacheKey = `${CACHE_PREFIX}${window.location.pathname}?${sortedParams}`;

      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey));
        if (cached && cached.rawItems === this.rawItems && Date.now() - cached.time < CACHE_TTL) {
          return cached.positions;
        }
      } catch (error) {
        // Storage unavailable or corrupt: load the index again.
      }

      const windowCount = Math.ceil(this.rawItems / INDEX_WINDOW);
      if (windowCount > MAX_INDEX_WINDOWS) throw new Error(`Too many products for the visibility index (${this.rawItems})`);

      const pages = Array.from({ length: windowCount }, (_, i) => i + 1);
      const windows = await mapLimit(pages, MAX_PARALLEL_REQUESTS, async (page) => {
        const content = await this.fetchSection(this.dataset.indexSectionId, page, signal);
        const el = content.querySelector('.js-visibility-index');
        if (!el) throw new Error('Visibility index section missing');
        return {
          items: Number(el.dataset.items),
          offset: Number(el.dataset.offset),
          ids: el.dataset.ids ? el.dataset.ids.split(',') : [],
          flags: (el.dataset.flags || '').replace(/\s/g, '')
        };
      });
      const positions = VirtualPaging.buildIndex(windows, this.rawItems);

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), rawItems: this.rawItems, positions }));
      } catch (error) {
        // Quota exceeded or storage blocked: the index is simply not cached.
      }
      return positions;
    }

    /**
     * Finds a card by item id, in case the sort order shifted between requests.
     * @param {string} id - Item id ("product-123", "article-45").
     * @returns {HTMLElement|undefined}
     */
    findCardById(id) {
      return [...this.cards.values()].find((li) => li.dataset.itemId === id);
    }

    /**
     * Renders a virtual page into the grid.
     * @param {number} page - Virtual page (1-based).
     * @param {boolean} append - Append to the grid instead of replacing the products.
     * @param {AbortSignal} signal - Abort signal.
     */
    async showPage(page, append, signal) {
      const { start, end } = VirtualPaging.range(page, this.total, this.perPage, this.promoSlots);
      const slice = this.positions.slice(start, end);

      const missing = VirtualPaging.rawPages(slice, this.perPage).filter((raw) => !this.loadedRawPages.has(raw));
      await mapLimit(missing, MAX_PARALLEL_REQUESTS, async (raw) => {
        this.collectCards(await this.fetchSection(this.dataset.sectionId, raw, signal));
        this.loadedRawPages.add(raw);
      });
      if (signal.aborted) throw new Error('Aborted');
      if (!this.isActive && this.timedOut) throw new Error('Timed out');

      const used = new Set(append
        ? [...this.grid.querySelectorAll(':scope > li[data-item-id]')].map((li) => li.dataset.itemId)
        : []);
      const products = [];
      slice.forEach((pos) => {
        let card = this.cards.get(pos.raw);
        if (!card || card.dataset.itemId !== pos.id) card = this.findCardById(pos.id);
        if (!card || used.has(pos.id)) return;
        used.add(pos.id);
        products.push(card);
      });

      if (!append) {
        this.grid.querySelectorAll(':scope > li[data-raw-index], :scope > li[data-promo-position]').forEach((li) => li.remove());
      }
      const items = page === 1 && !append ? VirtualPaging.interleavePromos(products, this.promos) : products;
      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      this.grid.insertBefore(fragment, this.grid.querySelector(':scope > li[data-promo-bottom]'));

      this.lastPage = page;
      const customPagination = this.closest('custom-pagination');
      if (customPagination && customPagination.reload && !append) customPagination.reload();
    }

    showEmpty() {
      const message = document.createElement('p');
      message.textContent = this.dataset.textEmpty;
      this.grid.replaceWith(message);
      this.lastPage = 1;
    }

    updateCounts() {
      const template = this.total === 1 ? this.dataset.textResultsOne : this.dataset.textResultsOther;
      document.querySelectorAll('.js-close-drawer-mob').forEach((button) => {
        button.textContent = template.replaceAll('[count]', this.total);
      });
      this.results.dataset.numResults = this.total;

      // The banner shows the collection's product count, so only update it for the unfiltered set.
      const isFiltered = [...this.params.keys()].some((key) => key.startsWith('filter.'));
      if (!isFiltered) {
        document.querySelectorAll('.js-collection-product-count').forEach((el) => {
          if (el.dataset.text) el.textContent = el.dataset.text.replaceAll('[count]', this.total);
        });
      }
    }

    updatePagination() {
      const pagination = this.results.querySelector('.js-pagination');
      if (!pagination) return;

      if (this.pages <= 1) {
        (pagination.closest('nav') || pagination).remove();
        return;
      }

      const page = this.lastPage;
      const hasMore = page < this.pages;
      pagination.dataset.isMoreResults = String(hasMore);

      if (this.dataset.paginationStyle === 'modern') {
        const { end } = VirtualPaging.range(page, this.total, this.perPage, this.promoSlots);
        const message = pagination.querySelector('.js-pagination-message');
        if (message) {
          message.textContent = this.dataset.textPositionResults
            .replaceAll('[visible_results]', end)
            .replaceAll('[total_results]', this.total);
        }
        const bar = pagination.querySelector('.js-pagination-bar');
        if (bar) bar.style.setProperty('--pagination-percent', `${Math.round((end / this.total) * 1000) / 10}%`);
        const loadMore = pagination.querySelector('.js-pagination-load-more');
        if (loadMore && hasMore) loadMore.href = this.pageUrl(page + 1);
        pagination.classList.toggle('hidden', !hasMore);
        return;
      }

      const setLink = (link, href) => {
        if (!link) return;
        if (href) {
          link.href = href;
          link.removeAttribute('role');
          link.removeAttribute('aria-disabled');
        } else {
          link.removeAttribute('href');
          link.setAttribute('role', 'link');
          link.setAttribute('aria-disabled', 'true');
        }
      };
      setLink(pagination.querySelector('.pagination__arrow--prev'), page > 1 ? this.pageUrl(page - 1) : null);
      const next = pagination.querySelector('.pagination__arrow--next');
      setLink(next, hasMore ? this.pageUrl(page + 1) : null);

      const position = pagination.querySelector('.pagination__item.text-center');
      if (position) {
        position.textContent = this.dataset.textPosition
          .replaceAll('[current_page]', page)
          .replaceAll('[total_pages]', this.pages);
      }

      const instant = pagination.querySelector('.pagination__page-link[data-instant], .pagination__arrow[data-instant]') !== null;
      pagination.querySelectorAll(':scope > li:not(.pagination__item)').forEach((li) => li.remove());
      const nextItem = next ? next.closest('li') : null;
      VirtualPaging.parts(page, this.pages).forEach((part) => {
        const li = document.createElement('li');
        li.className = 'hidden md:block';
        let el;
        if (part === null) {
          el = document.createElement('span');
          el.className = 'block leading-none ml-2 mr-2';
          el.textContent = '…';
        } else if (part === page) {
          el = document.createElement('span');
          el.className = 'pagination__page-current font-bold block leading-none';
          el.textContent = part;
        } else {
          el = document.createElement('a');
          el.className = 'pagination__page-link text-current block leading-none';
          el.href = this.pageUrl(part);
          el.textContent = part;
          if (instant) el.setAttribute('data-instant', '');
        }
        li.appendChild(el);
        pagination.insertBefore(li, nextItem);
      });
    }

    dispatchUpdated() {
      document.dispatchEvent(new CustomEvent('on:virtual-pagination:updated', { bubbles: true }));
    }
  }

  customElements.define('virtual-pagination', VirtualPagination);

  // Without a virtual pagination on the page (single-page result) nothing will be replaced
  if (!document.querySelector('virtual-pagination')) {
    document.querySelectorAll('[data-vp-pending]').forEach((el) => el.removeAttribute('data-vp-pending'));
  }
})();
